import { PAIGE_ACCENT, accentContrast, readableAccent } from '@paige/capturepoint';
import { accentOfColor, type AccentOfColor } from '@paige/ui';

export type LookMode = 'light' | 'dark';

/** The variables the Paige layer reads a picked colour from (screens/fleet/scene.css), all set or none. */
export const PICKED_VARS = [
  '--paige-picked-accent',
  '--paige-picked-accent-soft',
  '--paige-picked-accent-text',
  '--paige-picked-link',
  '--paige-picked-focus-halo',
] as const;

/** The canvas each theme paints (CapturePoint's `--cp-canvas`), which a mark has to stand out from. */
const CANVAS: Record<LookMode, string> = { light: '#f0f0f0', dark: '#101010' };

/** A colour the person picked that is not Paige blue itself; Paige blue, or none, is the layer's own default. */
const pickOf = (color: string | null, mode: LookMode): string | null =>
  color && color.toLowerCase() !== PAIGE_ACCENT[mode] ? color.toLowerCase() : null;

/**
 * The Paige layer's accent for a picked colour (Dustin, 2026-10-20: one accent everywhere). Marks use the colour
 * itself while it stands out from the canvas (3:1), else the workbench's own readable shade of it
 * (shared/accent-colors.ts `readableAccent`); links always use the readable shade (4.5:1), so a pale pick never
 * turns text invisible. Empty for Paige blue: the layer's hand-set blues stay exactly as they are.
 */
export function paigeLayerVars(color: string | null, mode: LookMode): Record<string, string> {
  const pick = pickOf(color, mode);
  if (!pick) return {};
  const ink = readableAccent(pick, mode);
  const mark = accentContrast(pick, CANVAS[mode]) >= 3 ? pick : ink;
  const onMark =
    accentContrast(mark, '#ffffff') >= accentContrast(mark, '#0b1620') ? '#ffffff' : '#0b1620';
  return {
    '--paige-picked-accent': mark,
    '--paige-picked-accent-soft': `${mark}24`,
    '--paige-picked-accent-text': onMark,
    '--paige-picked-link': ink,
    '--paige-picked-focus-halo': `${mark}59`,
  };
}

/** The packages/ui tokens' hue and chroma for a picked colour; null keeps their own Paige blue. */
export function tokenAccent(color: string | null, mode: LookMode): AccentOfColor | null {
  const pick = pickOf(color, mode);
  return pick ? accentOfColor(pick) : null;
}
